/**
 * Sidebar event-filtering helpers.
 *
 * Extracted from App.tsx so the per-tab filtering stays small and testable and
 * the component-level handler does not exceed the function-length standard.
 */
import { UnscheduledEvent, FilterOptions } from '../types';
import { EVENT_STATUS } from '../constants';

/** Number of days a Scheduled event may be away and still show in the sidebar. */
const SCHEDULED_WINDOW_DAYS = 7;

/**
 * True when the event is in an actively-worked state (IN_WORK or PENDING).
 */
function isActive(event: UnscheduledEvent): boolean {
  return event.status === EVENT_STATUS.IN_WORK || event.status === EVENT_STATUS.PENDING;
}

/**
 * True when the event passes the shared aircraft/carrier/station filters.
 */
function matchesCommonFilters(event: UnscheduledEvent, filters: FilterOptions): boolean {
  if (filters.aircraft && event.tailNumber !== filters.aircraft) return false;
  if (filters.carrier && event.carrier !== filters.carrier) return false;
  if (filters.station && event.station !== filters.station) return false;
  return true;
}

/**
 * True when a Scheduled event should appear: in work now, or scheduled within
 * the +/- window around today.
 */
function isScheduledVisible(event: UnscheduledEvent, nowMs: number): boolean {
  if (event.status === EVENT_STATUS.IN_WORK) return true;
  if (event.status === EVENT_STATUS.PENDING && event.timeDeclaredZulu) {
    const normalized =
      event.timeDeclaredZulu.replace(' ', 'T') + (event.timeDeclaredZulu.includes('Z') ? '' : 'Z');
    const schedDate = new Date(normalized);
    if (!isNaN(schedDate.getTime())) {
      const windowMs = SCHEDULED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
      return Math.abs(schedDate.getTime() - nowMs) <= windowMs;
    }
  }
  return false;
}

/**
 * Returns true when an event matches the given sidebar tab's record-type and
 * status rules (plus the shared common filters).
 */
function matchesSidebarTab(
  event: UnscheduledEvent,
  sidebarTab: string,
  filters: FilterOptions,
  nowMs: number,
): boolean {
  if (!matchesCommonFilters(event, filters)) return false;

  switch (sidebarTab) {
    case 'unscheduled':
      return (!event.recordType || event.recordType === 'Unscheduled') && isActive(event);
    case 'planned':
      return event.recordType === 'Planned' && isActive(event);
    case 'deferred':
      return event.recordType === 'Deferred' && isActive(event);
    case 'scheduled':
      return event.recordType === 'Scheduled' && isScheduledVisible(event, nowMs);
    default:
      return false;
  }
}

/**
 * Filters the events list down to the records shown for the active sidebar tab.
 */
export function getSidebarFilteredEvents(
  events: UnscheduledEvent[],
  sidebarTab: string,
  filters: FilterOptions,
  nowMs: number = Date.now(),
): UnscheduledEvent[] {
  return events.filter((event) => matchesSidebarTab(event, sidebarTab, filters, nowMs));
}
